import React from 'react';

import { Flex } from '@chakra-ui/react';
import { Chip, MenuItem, Select } from '@material-ui/core';
import TreeViewComponent from 'screens/admin/Competencies/common/TreeViewComponent';
import { findObject, getArrayOfIdsOfParentFunctions } from 'screens/admin/FunctionAndGrade/Helpers';
import styled, { css } from 'styled-components';
import { StyledTooltip } from 'styles/common';

import { CloseIcon2, DownChevronSvgIcon } from 'constants/icons';
import theme from 'constants/theme';

import { Typography13, Typography2 } from 'uiToolkit/Typography';
import { Button } from 'uiToolkitV1/Button';

const StyledFunctionSelect = styled(Select)`
  ${(props) =>
    props.$isAccessControl &&
    css`
      margin-right: 1.5rem;
    `}
  ${(props) =>
    props.$isDropdownOnly &&
    !props.$isAccessControl &&
    css`
      margin-top: 9px;
    `}
  ${(props) =>
    !props.$isDropdownOnly &&
    css`
      // width: 240px;
      font-size: 14px;
      .MuiOutlinedInput-notchedOutline {
        border: 1px solid ${(props) => props.theme.palette.primary.main} !important;
      }
    `}
  height: 40px;

  .MuiMenu-paper {
    max-height: 50vh;
    overflow-y: scroll;
    min-width: 250px !important;
  }
  border: 1px solid transparent !important;
  &:hover {
    border: 1px solid ${(props) => props.theme.palette.grey.main} !important;
    .function-select {
      display: flex !important;
    }
  }

  &:focus {
    border: 1px solid red !important;
    .function-select {
      display: flex !important;
    }
  }

  .MuiOutlinedInput-notchedOutline {
    border: none !important;
  }

  .MuiSelect-outlined {
    padding: 0.5rem 1rem;
    background-color: white !important;
    &:focus {
      background-color: white !important;
    }
  }

  .MuiSelect-icon {
    color: ${(props) => props.theme.palette.primary.main};
  }
  .function-select {
    display: none !important;
  }
`;

const VerticalDivider = styled.span`
  height: 1.3rem;
  width: 0px;
  border-right: 1px solid #ccc;
  margin-left: 0.5rem;
  margin-right: 0rem;
`;

const HorizontalDivider = styled.div`
  height: 1px;
  width: 100%;
  background: #ccc;
  border-radius: 2px;
  border-radius: 8px;
`;

const getSelectedFunctionTree = ({ functions = [], jobFunctionId }) => {
  const jobFunctionObject = findObject(functions ?? [], String(jobFunctionId));

  if (!jobFunctionObject?.parentId) {
    return jobFunctionObject?.name;
  }

  const arrayIds = getArrayOfIdsOfParentFunctions(functions, jobFunctionObject)?.reverse();
  const stringifiedFunctions =
    arrayIds?.reduce((prev, curr) => `${prev + curr?.name} > `, '') + jobFunctionObject?.name;
  return stringifiedFunctions;
};

export const FunctionDropdown = ({
  setSelectedFunction,
  selectedFunction = null,
  functions = [],
  loadingFunctions,
  isOpen,
  setIsOpen,
  isDropdownOnly = false,
  isAccessControl = false,
  selectedFunctionsArray = [],
  selectedFunctions = {},
  onChipDelete = () => {},
  placeholder = 'Function',
  isRowClickable = false,
}) => (
  <StyledFunctionSelect
    fullWidth
    displayEmpty
    variant="outlined"
    id="job-function-select"
    placeholder={placeholder}
    onChange={(e) => setSelectedFunction(e.target.value)}
    value={isAccessControl ? selectedFunctionsArray : selectedFunction}
    $isDropdownOnly={isDropdownOnly}
    $isAccessControl={isAccessControl}
    renderValue={(value) => {
      if (isAccessControl) {
        if (!value?.length) {
          return <Typography2 customColor={theme.palette.text.label}>Select Function</Typography2>;
        }
        return (
          <>
            {value?.map(({ id, displayName }) => (
              <Chip
                style={{ marginRight: '0.35rem', width: '100%' }}
                key={id}
                label={displayName}
                clickable={false}
                deleteIcon={
                  <CloseIcon2
                    rightGutter={0.5}
                    leftGutter={0.5}
                    size="fixed12px"
                    color={theme.palette.icons.grey2}
                    clickable
                  />
                }
                onDelete={(e) => {
                  e.stopPropagation();
                  onChipDelete({ uuid: id, name: displayName });
                }}
              />
            ))}
          </>
        );
      }
      if (value === null) {
        return <Typography2 customColor={theme.palette.primary.main}> - </Typography2>;
      }

      if (isDropdownOnly) {
        const selectedFunctionString = getSelectedFunctionTree({
          functions,
          jobFunctionId: value,
        });
        const isPlaceholderVisible = !selectedFunctionString;
        return (
          <StyledTooltip title={isPlaceholderVisible ? '' : selectedFunctionString}>
            {isPlaceholderVisible ? (
              <Typography2 customColor="hsl(0,0%,50%)">{placeholder}</Typography2>
            ) : (
              <span> {selectedFunctionString}</span>
            )}
          </StyledTooltip>
        );
      }
      const selectedFunction = findObject(functions ?? [], value);
      return <Typography2 customColor={theme.palette.text.primary}>{selectedFunction?.name}</Typography2>;
    }}
    MenuProps={{
      disablePortal: true,
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'left',
      },
      transformOrigin: {
        vertical: 'top',
        horizontal: 'left',
      },
      getContentAnchorEl: null,
    }}
    style={{ marginLeft: 'auto' }}
    disabled={loadingFunctions}
    open={isOpen}
    onClick={() => {
      setIsOpen((prev) => !prev);
    }}
    IconComponent={() => (
      <div className="function-select" style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}>
        <VerticalDivider />
        <div style={{ padding: '8px', display: 'flex' }}>
          <DownChevronSvgIcon color="#ccc" size="fixed20px" />
        </div>
      </div>
    )}
    multiple={isAccessControl}
  >
    <MenuItem value="" disabled style={{ display: 'none' }}>
      <Typography13 color="primary"> Select </Typography13>
    </MenuItem>
    {!isDropdownOnly && (
      <MenuItem value={null} disabled style={{ display: 'none' }}>
        <Typography13 color="primary"> - </Typography13>
      </MenuItem>
    )}
    <TreeViewComponent
      setSelectedFunction={setSelectedFunction}
      selectedFunction={selectedFunction}
      functions={functions}
      loadingFunctions={loadingFunctions}
      setIsOpen={setIsOpen}
      isDropdownOnly={isDropdownOnly}
      isAccessControl={isAccessControl}
      selectedFunctions={selectedFunctions}
      isRowClickable={isRowClickable}
    />
    <HorizontalDivider />
    <Flex justifyContent="flex-end">
      <Button onClick={() => setSelectedFunction(null)} variant="tertiary" p="6px">
        Reset
      </Button>
    </Flex>
  </StyledFunctionSelect>
);

export default FunctionDropdown;
